<script setup lang="ts">
import { type Snackbar, SnackbarLocation } from './types'

type Props = {
  list: Snackbar[]
  limit?: number
}

const props = withDefaults(defineProps<Props>(), {
  limit: 3,
})

const limitedList = computed(() => props.list.slice(-props.limit))

const groups = computed(() => {
  return {
    top: {
      type: SnackbarLocation.TOP,
      list: getGroupByLocation(SnackbarLocation.TOP),
    },
    bottom: {
      type: SnackbarLocation.BOTTOM,
      list: getGroupByLocation(SnackbarLocation.BOTTOM),
    },
  }
})

const getGroupByLocation = (location: SnackbarLocation) => {
  return limitedList.value.filter((snackbar) => snackbar.location === location)
}
</script>

<template>
  <Teleport to="body">
    <transition name="ui-fade">
      <div
        v-if="limitedList?.length"
        class="app-snackbar"
        data-test-id="snackbar"
      >
        <div
          v-for="group in groups"
          :key="group.type"
          class="app-snackbar__group"
          data-test-id="snackbar-group"
        >
          <transition-group name="ui-list">
            <the-snackbar-item
              v-for="snackbar in group.list"
              :key="snackbar.uuid"
              class="app-snackbar__item"
              :type="snackbar.type"
              :theme="snackbar.theme"
              :message="snackbar.message"
              :hyphens="snackbar.hyphens"
            />
          </transition-group>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style lang="scss" scoped>
@use '../../assets/ui-layer/styles/mixins' as *;

.app-snackbar {
  position: fixed;
  top: 60px;
  right: 0;
  bottom: 8px;
  left: 0;
  z-index: 10001;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: space-between;
  pointer-events: none;
  user-select: none;

  @include media('>=tablet') {
    top: 157px;
  }

  &__group {
    display: flex;
    gap: 16px;
    width: 100%;

    &:first-child {
      flex-direction: column;
    }

    &:last-child {
      flex-direction: column-reverse;
    }
  }

  &__item {
    left: 50%;
    width: 100%;
    min-width: 335px;
    max-width: 335px;
    min-height: 48px;
    margin: 0 auto;

    @include media('>=desktop-mini') {
      width: auto;
      min-width: auto;
      max-width: 360px;
    }
  }
}
</style>
